import { DOCUMENT, inject, NgZone, Service } from '@angular/core';
import { OAuthErrorEvent, OAuthService } from 'angular-oauth2-oidc';
import { filter } from 'rxjs/operators';

import { AuthService } from './auth.service';
import { AuthState } from '../state/auth.state';

const REFRESH_ERROR_TYPES = new Set([
  'silent_refresh_error',
  'silent_refresh_timeout',
  'token_refresh_error',
]);

@Service()
export class SessionWatcher {
  private readonly oauthService = inject(OAuthService);
  private readonly authState = inject(AuthState);
  private readonly authService = inject(AuthService);
  private readonly ngZone = inject(NgZone);
  private readonly document = inject(DOCUMENT);

  private inFlightRefresh: Promise<boolean> | null = null;
  private failureHandled = false;
  private started = false;

  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;

    this.oauthService.events
      .pipe(filter((event) => event instanceof OAuthErrorEvent))
      .subscribe((event) => {
        if (REFRESH_ERROR_TYPES.has(event.type)) {
          this.handleAuthFailure();
        }
      });

    const window = this.document.defaultView;
    if (!window) {
      return;
    }

    const onWake = (): void => {
      if (this.document.visibilityState !== 'visible') {
        return;
      }
      void this.ensureFreshAccessToken();
    };

    this.ngZone.runOutsideAngular(() => {
      this.document.addEventListener('visibilitychange', onWake);
      window.addEventListener('focus', onWake);
      window.addEventListener('online', onWake);
    });
  }

  ensureFreshAccessToken(): Promise<boolean> {
    if (this.oauthService.hasValidAccessToken()) {
      return Promise.resolve(true);
    }
    if (this.inFlightRefresh) {
      return this.inFlightRefresh;
    }
    this.inFlightRefresh = this.oauthService
      .silentRefresh()
      .then(() => this.oauthService.hasValidAccessToken())
      .catch(() => {
        this.handleAuthFailure();
        return false;
      })
      .finally(() => {
        this.inFlightRefresh = null;
      });
    return this.inFlightRefresh;
  }

  private handleAuthFailure(): void {
    if (this.failureHandled) {
      return;
    }
    this.failureHandled = true;
    this.authState.clear();
    this.authService.login();
  }
}
